<template>
  <div class="flex flex-col gap-2" @click.stop @mousedown.stop>
    <div
      v-if="showBackButton"
      class="flex items-center gap-2 px-2 py-2 cursor-pointer"
      @click="$emit('go-back')"
    >
      <ThemeImage
        :icon="imageNames.BACK_ARROW_WHITE"
        class="size-4"
        alt="go_back"
      />
      <span class="text-sm font-semibold text_color">Permissions</span>
    </div>
    <span class="color_666 text-[13px] leading-[17px]"
      >Who can access this view</span
    >
    <a-radio-group
      v-model:value="localAccessLevel"
      class="flex flex-col gap-2 radio_group_start"
    >
      <a-radio :value="accessLevels.EVERYONE">
        <div class="flex flex-col gap-1 proxima_nova">
          <span class="text_color text-sm font-semibold">Everyone</span>
          <span class="color_666 text-xs"
            >Everybody in your workspace will be able to access this view</span
          >
        </div>
      </a-radio>
      <a-radio :value="accessLevels.TEAM">
        <div class="flex flex-col gap-1 proxima_nova">
          <span class="text_color text-sm font-semibold"
            >Specific to team members</span
          >
          <span class="color_666 text-xs"
            >You and only people you add will be able to access this view</span
          >
        </div>
      </a-radio>
      <div v-if="localAccessLevel === accessLevels.TEAM" class="pt-1 ml-5">
        <UsersTeamsSelectDropdown
          ref="usersTeamsDropdownRef"
          v-model="selectedMembersLocal"
          :options-by-tab="accessOptionsByTab"
          :non-removable-items="currentUserAsNonRemovable"
          :enable-auto-select-current-user="enableAutoSelectCurrentUser"
          :initial-selection-by-tab="localSelectionByTab"
          :position="dropdownPosition"
          @selection-change="handleSelectionChange"
        />
        <div
          v-if="selectedCountNumbers"
          class="flex items-center gap-1 mt-2 px-2"
        >
          <img
            class="size-4"
            :src="imagePath.neutral.grey.infoIconGrey"
            alt="info_icon"
          />
          <span class="text-xs color_666">
            <span class="font-semibold">{{ selectedCountNumbers }}</span>
            <span> have access</span>
          </span>
        </div>
      </div>
      <a-radio :value="accessLevels.PRIVATE">
        <div class="flex flex-col gap-1 proxima_nova">
          <span
            class="text_color text-sm font-semibold flex items-center gap-1"
          >
            Private to me
            <ThemeImage
              class="size-3.5"
              :icon="imageNames.LOCK_GREY"
              alt="private"
            />
          </span>
          <span class="color_666 text-xs"
            >You only will be able to access this view</span
          >
        </div>
      </a-radio>
    </a-radio-group>

  </div>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import UsersTeamsSelectDropdown from "@/src/components/view_components/UsersTeamsSelectDropdown.vue";
import imagePath from "@/src/paths/imagePath";
import { accessLevels } from "@/src/constants/viewConstants";

const props = defineProps({
  accessLevel: { type: String, default: "everyone" },
  accessOptionsByTab: {
    type: Object,
    default: () => ({ Users: [], Teams: [] }),
  },
  selectedMembers: { type: Array, default: () => [] },
  currentUserAsNonRemovable: { type: Array, default: () => [] },
  enableAutoSelectCurrentUser: { type: Boolean, default: true },
  selectionByTab: {
    type: Object,
    default: () => ({ users: [], teams: [] }),
  },
  showBackButton: { type: Boolean, default: false },
  dropdownPosition: { type: String, default: "start" },
});

const emit = defineEmits([
  "update:accessLevel",
  "update:selectedMembers",
  "selection-change",
  "go-back",
]);

const localAccessLevel = ref(props.accessLevel || accessLevels.EVERYONE);
const selectedMembersLocal = ref([...props.selectedMembers]);
const usersTeamsDropdownRef = ref(null);
const localSelectionByTab = ref({ users: [], teams: [] });
const isInternalUpdate = ref(false);

const selectedCountNumbers = computed(() => {
  const { users = [], teams = [] } = localSelectionByTab.value;

  const userCount = users.length;
  const teamCount = teams.length;

  const parts = [];

  if (userCount > 0) {
    parts.push(`${userCount} User${userCount > 1 ? "s" : ""}`);
  }

  if (teamCount > 0) {
    parts.push(`${teamCount} Team${teamCount > 1 ? "s" : ""}`);
  }

  return parts.length > 0 ? parts.join(", ") : 0;
});

const handleSelectionChange = (data) => {
  if (data.byTab) {
    isInternalUpdate.value = true;
    localSelectionByTab.value = data.byTab;
  }
  emit("selection-change", data);
};

watch(localAccessLevel, (newVal) => {
  if (!isInternalUpdate.value) {
    isInternalUpdate.value = true;
    emit("update:accessLevel", newVal);
  } else {
    isInternalUpdate.value = false;
  }
});

watch(
  selectedMembersLocal,
  (newVal) => {
    if (!isInternalUpdate.value) {
      isInternalUpdate.value = true;
      emit("update:selectedMembers", newVal);
    } else {
      isInternalUpdate.value = false;
    }
  },
  { deep: true },
);

watch(
  () => props.accessLevel,
  (newVal) => {
    if (isInternalUpdate.value) {
      isInternalUpdate.value = false;
      return;
    }
    isInternalUpdate.value = true;
    localAccessLevel.value = newVal;
  },
);

watch(
  () => props.selectedMembers,
  (newVal) => {
    if (isInternalUpdate.value) {
      isInternalUpdate.value = false;
      return;
    }
    isInternalUpdate.value = true;
    selectedMembersLocal.value = [...newVal];
  },
  { deep: true },
);

watch(
  () => props.selectionByTab,
  (newVal) => {
    if (isInternalUpdate.value) {
      isInternalUpdate.value = false;
      return;
    }
    if (newVal && (newVal.users?.length || newVal.teams?.length)) {
      localSelectionByTab.value = {
        users: [...(newVal.users || [])],
        teams: [...(newVal.teams || [])],
      };
    }
  },
  { deep: true, immediate: true },
);
</script>

<style scoped>
.radio_group_start :deep(.ant-radio-wrapper) {
  align-items: flex-start;
}

.radio_group_start :deep(.ant-radio) {
  margin-top: 2px;
}
</style>
